import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSession, signOut } from "next-auth/react";
import {
  Input,
  Button,
  Modal,
  theme,
  Space,
  Alert,
  Table,
  Tag,
  Form,
  Tabs,
  Divider,
  Typography,
  Dropdown,
  Popover,
  Spin,
} from "antd";
import {
  ExportOutlined,
  LoadingOutlined,
  LinkOutlined,
  TeamOutlined,
  PlusOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { getMyOrgs, createNewOrg, joinOrg } from "./api-clients/orgs";
import { targetLoginLinks } from "./api-clients/logins";
import { getMySubscription, startTrial } from "./api-clients/subscriptions";
import { updateUserEmail } from "./api-clients/user";
import type { Org } from "src/models";
import Image from "next/image";
import { VzblIcon } from "src/components/VzblIcon";

const { TabPane } = Tabs;
const { Text, Title } = Typography;

type SubscriptionData = {
  id: string;
  name: string;
  status: string;
  startDate: Date;
  endDate: Date;
  trialEndDate: Date | null;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
};

type OrgWithSubscription = Org & {
  subscription?: SubscriptionData;
};

export function OrgLauncherNew(props: {
  goToFirst?: boolean;
  onClose?: () => void;
}) {
  const { token } = theme.useToken();
  const router = useRouter();
  const isSwitching = router.pathname !== "/orgs";
  const [fetched, setFetched] = React.useState(false);
  const session = useSession();
  const [orgsVersion, setOrgsVersion] = React.useState(0);
  const [orgs, setOrgs] = React.useState<
    {
      id: string;
      role: "super" | "admin" | "user" | "viewer" | "blocked";
      name: string;
      signupEmail: string;
    }[]
  >([]);
  const [orgsWithSubscriptions, setOrgsWithSubscriptions] = React.useState<
    OrgWithSubscription[]
  >([]);
  const [isJoining, setJoining] = React.useState(false);
  const [joinError, setJoinError] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("create");
  const [form] = Form.useForm();
  const [joinForm] = Form.useForm();
  const [linkForm] = Form.useForm();

  const makeOrgLink = React.useCallback((orgId: string) => {
    if (window.location.pathname.startsWith("/orgs")) {
      return `/${orgId}/home`;
    }
    const parts = window.location.pathname.split("/");
    parts.shift();
    parts.shift();
    return `/${orgId}/${parts.join("/")}`;
  }, []);

  const initMyOrgs = React.useRef<false | number>(false);
  React.useEffect(() => {
    if (initMyOrgs.current !== orgsVersion) {
      initMyOrgs.current = orgsVersion;
      getMyOrgs().then(({ data }) => {
        console.log("Org data:", data);
        setOrgs(data);
        setFetched(true);
        if (props.goToFirst && data.length > 0)
          router.push(makeOrgLink(data[0].id));
      });
    }
  }, [router, orgsVersion, props.goToFirst, makeOrgLink]);

  // Fetch subscription data
  React.useEffect(() => {
    if (session.data?.user?.id) {
      getMySubscription()
        .then((orgsWithSubs) => {
          if (!orgsWithSubs) {
            setOrgsWithSubscriptions([]);
            return;
          }
          const mappedOrgs = orgsWithSubs.map((org) => ({
            ...org,
            subscription: org.subscription
              ? {
                  id: org.subscription.id,
                  name: org.subscription.name,
                  status: org.subscription.status,
                  startDate: new Date(org.subscription.startDate),
                  endDate: new Date(org.subscription.endDate),
                  trialEndDate: org.subscription.trialEndDate
                    ? new Date(org.subscription.trialEndDate)
                    : null,
                  currentPeriodStart: new Date(
                    org.subscription.currentPeriodStart
                  ),
                  currentPeriodEnd: new Date(org.subscription.currentPeriodEnd),
                  cancelAtPeriodEnd: org.subscription.cancelAtPeriodEnd,
                }
              : undefined,
          }));
          setOrgsWithSubscriptions(
            mappedOrgs as unknown as OrgWithSubscription[]
          );
        })
        .catch((error) => {
          console.error("Error fetching subscription:", error);
        });
    }
  }, [session.data?.user?.id]);

  const [isCreating, setCreating] = React.useState(false);
  const handleAddOrg = React.useCallback(async () => {
    try {
      const values = await form.validateFields();
      const { orgName, email } = values;

      if (!orgName || !orgName.trim()) {
        alert("Please enter an organization name");
        return;
      }

      // Check if user is a super admin
      const isSuperAdmin = session.data?.user?.superuser;

      // Only enforce trial subscription for non-super admins
      if (!isSuperAdmin && orgs.length > 0) {
        alert("You can only create one org during the trial period.");
        return;
      }

      setCreating(true);

      // If email is provided and different from current, update it
      if (email && email !== session.data?.user?.email) {
        await updateUserEmail(email);
        // Update session data
        if (session.data?.user) {
          session.data.user.email = email;
        }
      }

      // For non-super admins, ensure they have a trial subscription
      if (!isSuperAdmin) {
        const orgsWithSubs = await getMySubscription();
        if (!orgsWithSubs || orgsWithSubs.length === 0) {
          const trialResponse = await startTrial();
          if (!trialResponse) {
            throw new Error("Failed to create trial subscription");
          }
        }
      }

      // Create org (subscription ID will be handled by the backend)
      const orgResponse = await createNewOrg(
        email || session.data?.user?.email || "",
        orgName.trim()
      );

      if (!orgResponse?.id) throw new Error("Failed to create org");

      // Reload orgs from the backend
      setOrgsVersion((version) => version + 1);
      form.resetFields();

      // Redirect to the wizard when the org is created
      router.push(`/${orgResponse.id}/wizard/workspace-setup`);
    } catch (error: any) {
      alert("Failed to create org: " + (error?.message || "Unknown error"));
    } finally {
      setCreating(false);
    }
  }, [orgs.length, session.data?.user?.email, form]);

  const handleJoinOrg = React.useCallback(async () => {
    try {
      const values = await joinForm.validateFields();
      const { inviteCode } = values;

      if (!inviteCode || !inviteCode.trim()) return;

      setJoining(true);
      await joinOrg(inviteCode.trim());
      setOrgsVersion((version) => version + 1);
      joinForm.resetFields();
    } catch (error: any) {
      setJoinError(error?.message || "Failed to join organization");
    } finally {
      setJoining(false);
    }
  }, [joinForm]);

  const handleLoginLink = React.useCallback(async () => {
    try {
      const values = await linkForm.validateFields();
      const { loginCode } = values;

      if (!loginCode || !loginCode.trim()) return;

      const status = await targetLoginLinks(loginCode.trim());
      if (status) {
        alert("You will need to login again for changes to take effect...");
        signOut();
      } else {
        setJoinError("Unable to link accounts. Please contact support.");
      }
      linkForm.resetFields();
    } catch (error: any) {
      setJoinError(error?.message || "Failed to link accounts");
    }
  }, [linkForm]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (
        text: string,
        record: { id: string; role: string; name: string; signupEmail: string }
      ) => (
        <Link
          href={makeOrgLink(record.id)}
          target={isSwitching ? "_blank" : "_top"}
          onClick={() => props.onClose && setTimeout(props.onClose, 0)}
        >
          <Space>
            <ExportOutlined />
            <span
              style={{
                color:
                  record.role === "super" ? token.colorErrorText : "inherit",
              }}
            >
              {text || record.signupEmail}
            </span>
          </Space>
        </Link>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </Text>
      ),
    },
    {
      title: "Status",
      key: "subscription",
      render: (
        text: string,
        record: { id: string; role: string; name: string; signupEmail: string }
      ) => {
        const orgWithSub = orgsWithSubscriptions.find(
          (o) => o.id === record.id
        );
        const subscription = orgWithSub?.subscription;

        if (!subscription) return <Tag color="success">active</Tag>;

        const statusColors = {
          active: "success",
          trialing: "warning",
          canceled: "error",
          expired: "error",
          pending: "processing",
        };

        return (
          <Space>
            <Tag
              color={
                statusColors[
                  subscription.status as keyof typeof statusColors
                ] || "default"
              }
            >
              {subscription.status.charAt(0).toUpperCase() +
                subscription.status.slice(1)}
            </Tag>
            {subscription.status === "trialing" && (
              <Space>
                <span>
                  Ends{" "}
                  {new Date(
                    subscription.trialEndDate || ""
                  ).toLocaleDateString()}
                </span>
                <Link href={`/${record.id}/settings/subscriptions`}>
                  <Button type="link" size="small" style={{ padding: "0 8px" }}>
                    Manage
                  </Button>
                </Link>
              </Space>
            )}
          </Space>
        );
      },
    },
  ];

  const hasTrialOrg = orgsWithSubscriptions.some(
    (org) => org.subscription?.status === "trialing"
  );

  return (
    <>
      <Modal
        closable={false}
        onCancel={props.onClose}
        open={true}
        footer={<></>}
        bodyStyle={{
          maxHeight: "80vh",
          overflow: "auto",
          paddingRight: 8,
        }}
      >
        {props.goToFirst ? (
          <div>
            {fetched ? (
              orgs.length > 0 ? (
                "Redirecting to your org..."
              ) : (
                <div>
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <VzblIcon size={40} />
                    <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                      Welcome to VZBL
                    </Title>
                    <Text type="secondary" style={{ fontSize: 16 }}>
                      Get started for free
                    </Text>
                  </div>

                  <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    style={{
                      background: "#fff",
                      padding: "16px",
                      borderRadius: "8px",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <TabPane
                      tab={
                        <Space>
                          <PlusOutlined />
                          <span>Create</span>
                        </Space>
                      }
                      key="create"
                    >
                      <Form
                        form={form}
                        onFinish={handleAddOrg}
                        layout="vertical"
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {!session.data?.user?.email && (
                            <Form.Item
                              name="email"
                              label="Email"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter your email",
                                },
                                {
                                  type: "email",
                                  message: "Please enter a valid email",
                                },
                              ]}
                            >
                              <Input placeholder="Enter your email address" />
                            </Form.Item>
                          )}
                          <Form.Item
                            name="orgName"
                            rules={[
                              {
                                required: true,
                                message: "Please enter an organization name",
                              },
                            ]}
                          >
                            <Input placeholder="Organization Name" autoFocus />
                          </Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={isCreating}
                            size="large"
                          >
                            Create Organization
                          </Button>
                        </Space>
                      </Form>
                    </TabPane>

                    <TabPane
                      tab={
                        <Space>
                          <TeamOutlined />
                          <span>Join</span>
                        </Space>
                      }
                      key="join"
                    >
                      <Form
                        form={joinForm}
                        onFinish={handleJoinOrg}
                        layout="vertical"
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Form.Item
                            name="inviteCode"
                            rules={[
                              {
                                required: true,
                                message: "Please enter the invite code",
                              },
                            ]}
                          >
                            <Input placeholder="Enter invite code" />
                          </Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={isJoining}
                            size="large"
                          >
                            Join Organization
                          </Button>
                        </Space>
                      </Form>
                    </TabPane>

                    <TabPane
                      tab={
                        <Space>
                          <LinkOutlined />
                          <span>Link</span>
                        </Space>
                      }
                      key="link"
                    >
                      <Form
                        form={linkForm}
                        onFinish={handleLoginLink}
                        layout="vertical"
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Form.Item
                            name="loginCode"
                            rules={[
                              {
                                required: true,
                                message: "Please enter your login code",
                              },
                            ]}
                          >
                            <Input placeholder="Enter your primary account code" />
                          </Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            block
                            size="large"
                          >
                            Link Accounts
                          </Button>
                        </Space>
                      </Form>
                    </TabPane>
                  </Tabs>

                  {joinError && (
                    <Alert
                      message="Error"
                      description={joinError}
                      type="error"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}

                  <Divider />

                  <div style={{ textAlign: "center" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      If you think this message is an error, make sure you are
                      logging in with your correct GitHub or Google account and
                      try again.
                    </Text>

                    <div style={{ marginTop: 16 }}>
                      <Button size="small" onClick={() => signOut()}>
                        Sign out
                      </Button>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Image
                  src="/vzbl-logo.png"
                  alt="VZBL Logo"
                  width={120}
                  height={40}
                  style={{ marginBottom: 16 }}
                />
                <LoadingOutlined style={{ fontSize: 24 }} />
              </div>
            )}
          </div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "24px",
                alignItems: "center",
              }}
            >
              <Title level={4} style={{ margin: 0, fontWeight: 500 }}>
                Your Organizations
              </Title>
              <Space>
                {orgs.length > 0 && (
                  <>
                    {session.data?.user?.superuser && (
                      <Popover
                        trigger="click"
                        placement="bottom"
                        title="Create Org"
                        content={
                          isCreating ? (
                            <Spin />
                          ) : (
                            <Form
                              form={form}
                              onFinish={handleAddOrg}
                              layout="vertical"
                            >
                              <Form.Item
                                name="orgName"
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Please enter an organization name",
                                  },
                                ]}
                              >
                                <Input placeholder="Organization Name" />
                              </Form.Item>
                              <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={isCreating}
                              >
                                Create Organization
                              </Button>
                            </Form>
                          )
                        }
                      >
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          style={{ color: token.colorErrorText }}
                        >
                          Create
                        </Button>
                      </Popover>
                    )}
                    <Popover
                      trigger="click"
                      placement="bottom"
                      title="Join Org"
                      content={
                        isJoining ? (
                          <Spin />
                        ) : (
                          <Form
                            form={joinForm}
                            onFinish={handleJoinOrg}
                            layout="vertical"
                          >
                            <Form.Item
                              name="inviteCode"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter the invite code",
                                },
                              ]}
                            >
                              <Input placeholder="Enter invite code" />
                            </Form.Item>
                            <Button
                              type="primary"
                              htmlType="submit"
                              block
                              loading={isJoining}
                            >
                              Join Organization
                            </Button>
                          </Form>
                        )
                      }
                    >
                      <Button type="text" icon={<TeamOutlined />}>
                        Join
                      </Button>
                    </Popover>
                  </>
                )}
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={props.onClose}
                />
              </Space>
            </div>

            {initMyOrgs.current === orgsVersion && fetched ? (
              <>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    marginBottom: 16,
                  }}
                >
                  <Table
                    dataSource={orgs}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    loading={!fetched}
                    style={{
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  {orgs.length === 0 && hasTrialOrg && (
                    <Button
                      type="primary"
                      onClick={handleAddOrg}
                      loading={isCreating}
                      icon={<PlusOutlined />}
                    >
                      Create Organization
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "40px 0",
                }}
              >
                <LoadingOutlined style={{ fontSize: 24 }} />
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
